import React, { useEffect } from 'react';
import { Check, Shield, AlertTriangle, Clock, BookOpen, Gift, Download } from 'lucide-react';
import { CountdownTimer } from '../components/CountdownTimer';
import { PurchasePopup } from '../components/PurchasePopup';
import { CTAButton } from '../components/CTAButton';
import { TestimonialCard } from '../components/TestimonialCard';
import { initPixel, trackEvent } from '../utils/metaPixel';
export function SalesPage() {
  useEffect(() => {
    initPixel();
    trackEvent('ViewContent', {
      content_name: 'Megapack Matemática Lista',
      value: 13.99,
      currency: 'USD'
    });
  }, []);
  const testimonials = [{
    name: 'Laura Martínez',
    role: 'Docente de Primaria',
    location: 'Bogotá, Colombia',
    content: 'Antes pasaba mis domingos planificando. Con este pack, tengo actividades listas que a mis alumnos les encantan. Recuperé mi tiempo libre.',
    imageInitial: 'L',
    color: 'bg-purple-500'
  }, {
    name: 'Carlos Ruiz',
    role: 'Profesor de Secundaria',
    location: 'Santiago, Chile',
    content: 'Las evaluaciones A/B son una genialidad. Ya no tengo que inventar pruebas desde cero y mis estudiantes disfrutan más las clases con los juegos.',
    imageInitial: 'C',
    color: 'bg-blue-500'
  }, {
    name: 'Ana Sofía López',
    role: 'Maestra de 4to Grado',
    location: 'Lima, Perú',
    content: 'El material visual es precioso. Los chicos entienden más rápido cuando ven las láminas. Es la mejor inversión que hice este año.',
    imageInitial: 'A',
    color: 'bg-pink-500'
  }, {
    name: 'Roberto Méndez',
    role: 'Docente Rural',
    location: 'Córdoba, Argentina',
    content: 'Trabajo en tres escuelas y no me daba la vida. Este material me salvó. Es llegar, imprimir y dar una clase de calidad.',
    imageInitial: 'R',
    color: 'bg-green-500'
  }, {
    name: 'Patricia Vega',
    role: 'Coordinadora de Área',
    location: 'CDMX, México',
    content: 'Compré el pack para probar y terminé recomendándolo a todo mi equipo. La estructura de las planificaciones es impecable.',
    imageInitial: 'P',
    color: 'bg-orange-500'
  }];
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-900 pt-12" data-id="element-27">
      <CountdownTimer data-id="element-28" />
      <PurchasePopup data-id="element-29" />

      {/* Hero Section */}
      <header className="bg-white pb-12 pt-8 px-4" data-id="element-30">
        <div className="max-w-3xl mx-auto text-center" data-id="element-31">
          <div className="inline-block bg-yellow-100 text-yellow-800 px-4 py-1 rounded-full text-sm font-bold mb-6 uppercase tracking-wide" data-id="element-32">
            🧠 Golpe Emocional
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-6" data-id="element-33">
            Tu problema no es enseñar Matemática.{' '}
            <span className="text-red-600" data-id="element-34">
              Tu problema es tener que inventar todo desde cero, todos los días.
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed" data-id="element-35">
            Clases, actividades, evaluaciones y planificaciones listas para
            usar, para que dejes de improvisar y vuelvas a disfrutar enseñar.
          </p>

          <img src="https://cdn.magicpatterns.com/uploads/kcmJByj5VzzUcwDoY4W9SL/Cover_principal.png" alt="Megapack Matemática Lista Principal" className="w-full max-w-lg mx-auto rounded-xl shadow-2xl mb-10 transform hover:scale-105 transition-transform duration-500" data-id="element-36" />

          <CTAButton variant="primary" className="w-full md:w-auto text-xl px-10 py-5" data-id="element-37">
            QUIERO MI MEGAPACK AHORA
          </CTAButton>
        </div>
      </header>

      {/* Section 1: Identification */}
      <section className="py-16 px-4 bg-white" data-id="element-38">
        <div className="max-w-3xl mx-auto" data-id="element-39">
          <div className="flex items-center gap-3 mb-6" data-id="element-40">
            <div className="bg-blue-100 p-2 rounded-lg" data-id="element-41">
              <BookOpen className="w-6 h-6 text-blue-600" data-id="element-42" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 uppercase tracking-wide" data-id="element-43">
              Identificación (El Dolor Real)
            </h2>
          </div>

          <div className="prose prose-lg text-gray-700" data-id="element-44">
            <p className="font-medium text-xl" data-id="element-45">
              Sos docente. Te gusta enseñar. Te importa que tus estudiantes
              aprendan.
            </p>
            <p data-id="element-46">Pero tu realidad diaria es otra:</p>

            <ul className="space-y-4 my-8 list-none pl-0" data-id="element-47">
              <li className="flex items-start gap-3" data-id="element-48">
                <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-49" />
                <span data-id="element-50">
                  Llegás cansado/a a casa… y todavía tenés que preparar clases
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-51">
                <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-52" />
                <span data-id="element-53">Buscás ideas en internet y perdés horas</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-54">
                <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-55" />
                <span data-id="element-56">Armás actividades a las apuradas</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-57">
                <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-58" />
                <span data-id="element-59">Improvisás más de lo que te gustaría</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-60">
                <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-61" />
                <span data-id="element-62">
                  Sentís que podrías dar mejores clases… si tuvieras tiempo
                </span>
              </li>
            </ul>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg my-8" data-id="element-63">
              <p className="font-bold text-red-800 text-lg m-0" data-id="element-64">
                👉 Y lo peor: No estás agotado por enseñar. Estás agotado por
                tener que crear todo desde cero.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: The Problem */}
      <section className="py-16 px-4 bg-gray-100" data-id="element-65">
        <div className="max-w-3xl mx-auto" data-id="element-66">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-67">
            💥 EL PROBLEMA QUE NADIE DICE
          </h2>

          <div className="bg-white p-8 rounded-xl shadow-sm" data-id="element-68">
            <p className="text-lg text-gray-700 mb-6" data-id="element-69">
              La formación docente te enseñó a enseñar.
            </p>
            <p className="text-lg text-gray-700 mb-6" data-id="element-70">
              Pero nadie te dio el sistema listo para:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-id="element-71">
              <div className="flex items-center gap-2 bg-green-50 p-3 rounded border border-green-100" data-id="element-72">
                <Check className="w-5 h-5 text-green-600" data-id="element-73" />
                <span className="font-medium" data-id="element-74">
                  Tener actividades organizadas
                </span>
              </div>
              <div className="flex items-center gap-2 bg-green-50 p-3 rounded border border-green-100" data-id="element-75">
                <Check className="w-5 h-5 text-green-600" data-id="element-76" />
                <span className="font-medium" data-id="element-77">
                  Evaluar sin inventar pruebas
                </span>
              </div>
              <div className="flex items-center gap-2 bg-green-50 p-3 rounded border border-green-100" data-id="element-78">
                <Check className="w-5 h-5 text-green-600" data-id="element-79" />
                <span className="font-medium" data-id="element-80">
                  Planificar sin hoja en blanco
                </span>
              </div>
              <div className="flex items-center gap-2 bg-green-50 p-3 rounded border border-green-100" data-id="element-81">
                <Check className="w-5 h-5 text-green-600" data-id="element-82" />
                <span className="font-medium" data-id="element-83">
                  Tener recursos listos siempre
                </span>
              </div>
            </div>

            <p className="text-xl font-bold text-center text-gray-900" data-id="element-84">
              Por eso el desgaste no es pedagógico.
              <br data-id="element-85" />
              <span className="text-red-600" data-id="element-86">
                Es mental. Es estructural. Es acumulativo.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* Section 3: Consequences */}
      <section className="py-16 px-4 bg-white" data-id="element-87">
        <div className="max-w-3xl mx-auto" data-id="element-88">
          <div className="bg-gray-900 text-white p-8 rounded-2xl shadow-xl" data-id="element-89">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-3" data-id="element-90">
              <Clock className="w-8 h-8 text-yellow-400" data-id="element-91" />
              LO QUE PASA SI SEGUÍS ASÍ
            </h2>

            <p className="text-lg mb-6" data-id="element-92">Si todo sigue igual:</p>

            <ul className="space-y-4 mb-8" data-id="element-93">
              <li className="flex items-center gap-3 text-gray-300" data-id="element-94">
                <div className="w-2 h-2 bg-red-500 rounded-full" data-id="element-95"></div>
                Vas a seguir planificando a la noche
              </li>
              <li className="flex items-center gap-3 text-gray-300" data-id="element-96">
                <div className="w-2 h-2 bg-red-500 rounded-full" data-id="element-97"></div>
                Vas a seguir sintiendo que nunca es suficiente
              </li>
              <li className="flex items-center gap-3 text-gray-300" data-id="element-98">
                <div className="w-2 h-2 bg-red-500 rounded-full" data-id="element-99"></div>
                Vas a seguir trabajando el doble que deberías
              </li>
              <li className="flex items-center gap-3 text-gray-300" data-id="element-100">
                <div className="w-2 h-2 bg-red-500 rounded-full" data-id="element-101"></div>
                Vas a seguir postergando tu descanso
              </li>
            </ul>

            <p className="text-xl font-bold text-yellow-400 text-center border-t border-gray-700 pt-6" data-id="element-102">
              Y enseñar no debería sentirse así.
            </p>
          </div>

          <div className="mt-10 text-center" data-id="element-103">
            <CTAButton variant="secondary" data-id="element-104">
              SÍ, QUIERO DEJAR DE IMPROVISAR
            </CTAButton>
          </div>
        </div>
      </section>

      {/* Section 4: Solution */}
      <section className="py-16 px-4 bg-blue-50" data-id="element-105">
        <div className="max-w-4xl mx-auto" data-id="element-106">
          <div className="text-center mb-12" data-id="element-107">
            <h2 className="text-3xl md:text-4xl font-extrabold text-blue-900 mb-4" data-id="element-108">
              📘 MEGAPACK MATEMÁTICA LISTA™
            </h2>
            <p className="text-xl text-blue-700 font-medium" data-id="element-109">
              El sistema completo que resuelve tu año de Matemática.
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg mb-12 text-center" data-id="element-110">
            <p className="text-lg text-gray-700 mb-4" data-id="element-111">
              No son ideas sueltas. No son PDFs decorativos. No es teoría.
            </p>
            <p className="text-2xl font-bold text-green-600" data-id="element-112">
              👉 Es material docente real, usable, organizado y listo.
            </p>
          </div>

          <h3 className="text-2xl font-bold text-center mb-10 text-gray-900" data-id="element-113">
            📦 ¿QUÉ INCLUYE EL MEGAPACK?
          </h3>

          <div className="space-y-12" data-id="element-114">
            {/* Main Ebook */}
            <div className="flex flex-col md:flex-row items-center gap-8 bg-white p-6 rounded-xl shadow-md" data-id="element-115">
              <div className="w-full md:w-1/3" data-id="element-116">
                <img src="https://cdn.magicpatterns.com/uploads/kcmJByj5VzzUcwDoY4W9SL/Cover_principal.png" alt="Ebook Principal" className="w-full rounded-lg shadow-md" data-id="element-117" />
              </div>
              <div className="w-full md:w-2/3" data-id="element-118">
                <h4 className="text-xl font-bold text-blue-800 mb-3" data-id="element-119">
                  📗 EBOOK PRINCIPAL — CLASES DE MATEMÁTICA LISTAS
                </h4>
                <ul className="space-y-2" data-id="element-120">
                  <li className="flex items-center gap-2" data-id="element-121">
                    <Check className="w-5 h-5 text-green-500" data-id="element-122" /> Secuencias
                    didácticas completas
                  </li>
                  <li className="flex items-center gap-2" data-id="element-123">
                    <Check className="w-5 h-5 text-green-500" data-id="element-124" /> Actividades
                    paso a paso
                  </li>
                  <li className="flex items-center gap-2" data-id="element-125">
                    <Check className="w-5 h-5 text-green-500" data-id="element-126" /> Inicio –
                    Desarrollo – Cierre
                  </li>
                  <li className="flex items-center gap-2" data-id="element-127">
                    <Check className="w-5 h-5 text-green-500" data-id="element-128" /> Variantes para
                    distintos niveles
                  </li>
                  <li className="flex items-center gap-2" data-id="element-129">
                    <Check className="w-5 h-5 text-green-500" data-id="element-130" /> Estrategias de
                    explicación
                  </li>
                </ul>
              </div>
            </div>

            {/* Bonus 1 */}
            <div className="flex flex-col md:flex-row items-center gap-8 bg-white p-6 rounded-xl shadow-md border-2 border-yellow-400 relative overflow-hidden" data-id="element-131">
              <div className="absolute top-0 right-0 bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1" data-id="element-132">
                GRATIS HOY
              </div>
              <div className="w-full md:w-1/3" data-id="element-133">
                <img src="https://cdn.magicpatterns.com/uploads/tH3Zx9iTHxbfYpZSYk8zo3/cover_bono_1.png" alt="Bono 1" className="w-full rounded-lg shadow-md" data-id="element-134" />
              </div>
              <div className="w-full md:w-2/3" data-id="element-135">
                <h4 className="text-xl font-bold text-blue-800 mb-3" data-id="element-136">
                  📘 BONO 1 — KIT DE EVALUACIONES PREMIUM
                </h4>
                <p className="text-gray-600 mb-4 italic" data-id="element-137">
                  Nunca más inventar una prueba desde cero.
                </p>
                <ul className="space-y-2" data-id="element-138">
                  <li className="flex items-center gap-2" data-id="element-139">
                    <Check className="w-5 h-5 text-green-500" data-id="element-140" /> Pruebas listas
                    por unidad
                  </li>
                  <li className="flex items-center gap-2" data-id="element-141">
                    <Check className="w-5 h-5 text-green-500" data-id="element-142" /> Versiones A/B
                  </li>
                  <li className="flex items-center gap-2" data-id="element-143">
                    <Check className="w-5 h-5 text-green-500" data-id="element-144" /> Claves de
                    corrección
                  </li>
                  <li className="flex items-center gap-2" data-id="element-145">
                    <Check className="w-5 h-5 text-green-500" data-id="element-146" /> Rúbricas claras
                  </li>
                </ul>
              </div>
            </div>

            {/* Bonus 2 */}
            <div className="flex flex-col md:flex-row items-center gap-8 bg-white p-6 rounded-xl shadow-md border-2 border-yellow-400 relative overflow-hidden" data-id="element-147">
              <div className="absolute top-0 right-0 bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1" data-id="element-148">
                GRATIS HOY
              </div>
              <div className="w-full md:w-1/3" data-id="element-149">
                <img src="https://cdn.magicpatterns.com/uploads/axx1H2KXp2c8d2LzCCNtgR/Cover_bono_2.png" alt="Bono 2" className="w-full rounded-lg shadow-md" data-id="element-150" />
              </div>
              <div className="w-full md:w-2/3" data-id="element-151">
                <h4 className="text-xl font-bold text-blue-800 mb-3" data-id="element-152">
                  🎲 BONO 2 — BANCO DE JUEGOS Y ACTIVIDADES
                </h4>
                <p className="text-gray-600 mb-4 italic" data-id="element-153">
                  Enganchás sin improvisar.
                </p>
                <ul className="space-y-2" data-id="element-154">
                  <li className="flex items-center gap-2" data-id="element-155">
                    <Check className="w-5 h-5 text-green-500" data-id="element-156" /> Juegos
                    matemáticos
                  </li>
                  <li className="flex items-center gap-2" data-id="element-157">
                    <Check className="w-5 h-5 text-green-500" data-id="element-158" /> Dinámicas
                    rápidas
                  </li>
                  <li className="flex items-center gap-2" data-id="element-159">
                    <Check className="w-5 h-5 text-green-500" data-id="element-160" /> Actividades
                    para enganchar
                  </li>
                  <li className="flex items-center gap-2" data-id="element-161">
                    <Check className="w-5 h-5 text-green-500" data-id="element-162" /> Recursos para
                    días difíciles
                  </li>
                </ul>
              </div>
            </div>

            {/* Bonus 3 */}
            <div className="flex flex-col md:flex-row items-center gap-8 bg-white p-6 rounded-xl shadow-md border-2 border-yellow-400 relative overflow-hidden" data-id="element-163">
              <div className="absolute top-0 right-0 bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1" data-id="element-164">
                GRATIS HOY
              </div>
              <div className="w-full md:w-1/3" data-id="element-165">
                <img src="https://cdn.magicpatterns.com/uploads/kLtXZeDvhknxvrmPoWHh4E/Cover_bono_3.png" alt="Bono 3" className="w-full rounded-lg shadow-md" data-id="element-166" />
              </div>
              <div className="w-full md:w-2/3" data-id="element-167">
                <h4 className="text-xl font-bold text-blue-800 mb-3" data-id="element-168">
                  🖼 BONO 3 — KIT VISUAL
                </h4>
                <p className="text-gray-600 mb-4 italic" data-id="element-169">
                  Explicás mejor, con menos desgaste.
                </p>
                <ul className="space-y-2" data-id="element-170">
                  <li className="flex items-center gap-2" data-id="element-171">
                    <Check className="w-5 h-5 text-green-500" data-id="element-172" /> Láminas
                  </li>
                  <li className="flex items-center gap-2" data-id="element-173">
                    <Check className="w-5 h-5 text-green-500" data-id="element-174" /> Posters
                  </li>
                  <li className="flex items-center gap-2" data-id="element-175">
                    <Check className="w-5 h-5 text-green-500" data-id="element-176" /> Material
                    imprimible
                  </li>
                  <li className="flex items-center gap-2" data-id="element-177">
                    <Check className="w-5 h-5 text-green-500" data-id="element-178" /> Apoyos visuales
                  </li>
                </ul>
              </div>
            </div>

            {/* Bonus 4 */}
            <div className="flex flex-col md:flex-row items-center gap-8 bg-white p-6 rounded-xl shadow-md border-2 border-yellow-400 relative overflow-hidden" data-id="element-179">
              <div className="absolute top-0 right-0 bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1" data-id="element-180">
                GRATIS HOY
              </div>
              <div className="w-full md:w-1/3" data-id="element-181">
                <img src="https://cdn.magicpatterns.com/uploads/5FwyvfFDQTAgnCygmFfbbP/Cover_bono_4.png" alt="Bono 4" className="w-full rounded-lg shadow-md" data-id="element-182" />
              </div>
              <div className="w-full md:w-2/3" data-id="element-183">
                <h4 className="text-xl font-bold text-blue-800 mb-3" data-id="element-184">
                  📅 BONO 4 — PLANIFICACIÓN PERFECTA
                </h4>
                <p className="text-gray-600 mb-4 italic" data-id="element-185">
                  Tu año organizado. Tu cabeza más liviana.
                </p>
                <ul className="space-y-2" data-id="element-186">
                  <li className="flex items-center gap-2" data-id="element-187">
                    <Check className="w-5 h-5 text-green-500" data-id="element-188" /> Calendario
                    anual
                  </li>
                  <li className="flex items-center gap-2" data-id="element-189">
                    <Check className="w-5 h-5 text-green-500" data-id="element-190" /> Secuencias
                    listas
                  </li>
                  <li className="flex items-center gap-2" data-id="element-191">
                    <Check className="w-5 h-5 text-green-500" data-id="element-192" /> Planificación
                    semanal editable
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Value Reframe */}
      <section className="py-16 px-4 bg-white" data-id="element-193">
        <div className="max-w-3xl mx-auto text-center" data-id="element-194">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 uppercase tracking-wide" data-id="element-195">
            🧩 LO QUE REALMENTE ESTÁS COMPRANDO
          </h2>

          <p className="text-xl text-gray-700 mb-8" data-id="element-196">
            No estás comprando PDFs. Estás comprando:
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12" data-id="element-197">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-198">
              <span className="block text-2xl mb-2" data-id="element-199">🧠</span>
              <span className="font-bold text-gray-800" data-id="element-200">Alivio mental</span>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-201">
              <span className="block text-2xl mb-2" data-id="element-202">⏳</span>
              <span className="font-bold text-gray-800" data-id="element-203">Tiempo recuperado</span>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-204">
              <span className="block text-2xl mb-2" data-id="element-205">📚</span>
              <span className="font-bold text-gray-800" data-id="element-206">Estructura</span>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-207">
              <span className="block text-2xl mb-2" data-id="element-208">🛠</span>
              <span className="font-bold text-gray-800" data-id="element-209">Recursos listos</span>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 col-span-2 md:col-span-2" data-id="element-210">
              <span className="block text-2xl mb-2" data-id="element-211">💬</span>
              <span className="font-bold text-gray-800" data-id="element-212">
                Seguridad frente al curso
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left" data-id="element-213">
            <div className="bg-green-50 p-6 rounded-lg border border-green-200" data-id="element-214">
              <h3 className="font-bold text-green-800 mb-4 flex items-center gap-2" data-id="element-215">
                <Check className="w-5 h-5" data-id="element-216" /> PARA QUIÉN ES
              </h3>
              <ul className="space-y-2 text-sm" data-id="element-217">
                <li data-id="element-218">✔ Docentes de primaria y secundaria</li>
                <li data-id="element-219">✔ Profes que trabajan en varias escuelas</li>
                <li data-id="element-220">✔ Quienes están cansados de improvisar</li>
                <li data-id="element-221">✔ Quienes quieren enseñar mejor sin trabajar más</li>
              </ul>
            </div>

            <div className="bg-red-50 p-6 rounded-lg border border-red-200" data-id="element-222">
              <h3 className="font-bold text-red-800 mb-4 flex items-center gap-2" data-id="element-223">
                <AlertTriangle className="w-5 h-5" data-id="element-224" /> PARA QUIÉN NO ES
              </h3>
              <ul className="space-y-2 text-sm" data-id="element-225">
                <li data-id="element-226">❌ Para quien disfruta crear todo desde cero</li>
                <li data-id="element-227">❌ Para quien no quiere usar material ya listo</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4 bg-gray-100" data-id="element-228">
        <div className="max-w-6xl mx-auto" data-id="element-229">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12" data-id="element-230">
            Lo que dicen otros docentes
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-231">
            {testimonials.map((t, i) => <TestimonialCard key={i} {...t} data-id="element-232" />)}
          </div>
        </div>
      </section>

      {/* Results & Guarantee */}
      <section className="py-16 px-4 bg-white" data-id="element-233">
        <div className="max-w-3xl mx-auto" data-id="element-234">
          <div className="mb-16" data-id="element-235">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2" data-id="element-236">
              <Gift className="w-6 h-6 text-yellow-500" data-id="element-237" /> ⚡ RESULTADO REAL
            </h2>
            <p className="mb-4" data-id="element-238">Cuando tenés estructura:</p>
            <ul className="space-y-3" data-id="element-239">
              <li className="flex items-center gap-2" data-id="element-240">
                <Check className="w-5 h-5 text-green-500" data-id="element-241" /> Planificás más
                rápido
              </li>
              <li className="flex items-center gap-2" data-id="element-242">
                <Check className="w-5 h-5 text-green-500" data-id="element-243" /> Llegás más
                tranquilo/a a clase
              </li>
              <li className="flex items-center gap-2" data-id="element-244">
                <Check className="w-5 h-5 text-green-500" data-id="element-245" /> Evaluás sin estrés
              </li>
              <li className="flex items-center gap-2" data-id="element-246">
                <Check className="w-5 h-5 text-green-500" data-id="element-247" /> Enseñás con más
                claridad
              </li>
              <li className="flex items-center gap-2" data-id="element-248">
                <Check className="w-5 h-5 text-green-500" data-id="element-249" /> Terminás el día con
                menos agotamiento mental
              </li>
            </ul>
          </div>

          <div className="bg-gray-50 border-2 border-gray-200 p-8 rounded-xl text-center" data-id="element-250">
            <Shield className="w-12 h-12 text-blue-600 mx-auto mb-4" data-id="element-251" />
            <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-252">
              🛡 GARANTÍA DE SATISFACCIÓN
            </h3>
            <p className="text-gray-600 mb-4" data-id="element-253">
              Probalo. Revisá los materiales. Usalos en clase.
            </p>
            <p className="text-gray-600 font-medium" data-id="element-254">
              Si sentís que no te ayuda a ahorrar tiempo y esfuerzo, podés
              solicitar garantía.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="compra" className="py-20 px-4 bg-gradient-to-b from-blue-900 to-blue-800 text-white text-center" data-id="element-255">
        <div className="max-w-3xl mx-auto" data-id="element-256">
          <div className="mb-8" data-id="element-257">
            <span className="bg-yellow-400 text-yellow-900 px-4 py-1 rounded-full text-sm font-bold uppercase" data-id="element-258">
              🎁 Bonus Psicológico
            </span>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-259">
            Tu vocación no necesita más sacrificio. Necesita más herramientas.
          </h2>

          <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 mt-10" data-id="element-260">
            <div className="flex flex-col items-center" data-id="element-261">
              <p className="text-gray-300 text-lg mb-2" data-id="element-262">
                Precio Regular: <span className="line-through" data-id="element-263">$47.00</span>
              </p>
              <p className="text-5xl font-extrabold text-white mb-2" data-id="element-264">
                $13.99 USD
              </p>
              <p className="text-green-400 font-bold mb-8" data-id="element-265">
                ¡Oferta por tiempo limitado!
              </p>

              <CTAButton variant="primary" className="w-full md:w-2/3 text-2xl py-6 animate-bounce-slow" isCheckout={true} data-id="element-266">
                QUIERO MI MEGAPACK MATEMÁTICA LISTA
              </CTAButton>

              <div className="flex items-center gap-2 mt-6 text-sm text-gray-400" data-id="element-267">
                <Shield className="w-4 h-4" data-id="element-268" />
                <span data-id="element-269">Pago 100% Seguro vía Hotmart</span>
              </div>

              <div className="mt-8 flex justify-center gap-4" data-id="element-270">
                <img src="https://cdn.magicpatterns.com/uploads/kcmJByj5VzzUcwDoY4W9SL/Cover_principal.png" className="w-20 h-20 object-cover rounded border border-white/20" alt="Miniatura" data-id="element-271" />
                <img src="https://cdn.magicpatterns.com/uploads/tH3Zx9iTHxbfYpZSYk8zo3/cover_bono_1.png" className="w-20 h-20 object-cover rounded border border-white/20" alt="Miniatura" data-id="element-272" />
                <img src="https://cdn.magicpatterns.com/uploads/axx1H2KXp2c8d2LzCCNtgR/Cover_bono_2.png" className="w-20 h-20 object-cover rounded border border-white/20" alt="Miniatura" data-id="element-273" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-gray-900 text-gray-500 py-8 text-center text-sm" data-id="element-274">
        <p data-id="element-275">
          © {new Date().getFullYear()} Matemática Lista™. Todos los derechos
          reservados.
        </p>
        <p className="mt-2" data-id="element-276">
          Este sitio no es parte del sitio web de Facebook o Facebook Inc.
        </p>
      </footer>
    </div>;
}